import React from 'react';
import { useTheme, type ColorTheme, type HeaderLayout, type HeaderStyle, type SidebarLayout, type SidebarStyle } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';

const Theming: React.FC = () => {
    const {
        colorTheme,
        headerLayout,
        headerStyle,
        sidebarLayout,
        sidebarStyle,
        direction,
        setColorTheme,
        setHeaderLayout,
        setHeaderStyle,
        setSidebarLayout,
        setSidebarStyle,
        setDirection
    } = useTheme();

    const colorThemes: { name: string; value: ColorTheme; color1: string; color2: string }[] = [
        { name: 'Default', value: 'default', color1: '#5840ef', color2: '#e46dce' },
        { name: 'Blue', value: 'blue', color1: '#0049e8', color2: '#4cbdff' },
        { name: 'Indigo', value: 'indigo', color1: '#4d10f2', color2: '#758bfd' },
        { name: 'Purple', value: 'purple', color1: '#471ca8', color2: '#d1105a' },
        { name: 'Pink', value: 'pink', color1: '#e50a8e', color2: '#b892ff' },
        { name: 'Red', value: 'red', color1: '#c80036', color2: '#e46dce' },
        { name: 'Orange', value: 'orange', color1: '#fc7a1e', color2: '#ffc96b' },
        { name: 'Yellow', value: 'yellow', color1: '#fcab16', color2: '#ffd12c' },
        { name: 'Green', value: 'green', color1: '#08a046', color2: '#0bbeeb' },
        { name: 'Teal', value: 'teal', color1: '#00a885', color2: '#c2cb6a' },
        { name: 'Cyan', value: 'cyan', color1: '#03aed2', color2: '#e46dce' },
        { name: 'Chocolate', value: 'chocolate', color1: '#411900', color2: '#36008d' },
        { name: 'Brown', value: 'brown', color1: '#795548', color2: '#ffc95f' },
        { name: 'Grey', value: 'grey', color1: '#61677a', color2: '#00adb5' },
        { name: 'Black', value: 'black', color1: '#333333', color2: '#e46dce' },
        { name: 'Pista', value: 'pista', color1: '#84ac79', color2: '#d2e66a' },
    ];

    const headerLayouts: { name: string; value: HeaderLayout; icon: string }[] = [
        { name: 'Standard', value: 'standard', icon: 'bi bi-arrow-clockwise' },
        { name: 'Boxed', value: 'boxed', icon: 'bi bi-box' },
    ];

    const headerStyles: { name: string; value: HeaderStyle; iconcolor: string }[] = [
        { name: 'Transparent', value: 'transparent', iconcolor: 'transparent' },
        { name: 'White', value: 'white', iconcolor: 'bg-white' },
        { name: 'Background', value: 'bg', iconcolor: 'bg-body-1' },
        { name: 'Theme', value: 'theme', iconcolor: 'bg-theme-1' },
        { name: 'Accent', value: 'accent', iconcolor: 'bg-theme-accent-1' },
    ];

    const sidebarLayouts: { name: string; value: SidebarLayout; icon: string }[] = [
        { name: 'Standard', value: 'standard', icon: 'bi bi-arrow-clockwise' },
        { name: 'Boxed', value: 'boxed', icon: 'bi bi-bezier' },
        { name: 'Iconic', value: 'iconic', icon: 'bi bi-box ' },
    ];

    const sidebarStyles: { name: string; value: SidebarStyle; iconcolor: string }[] = [
        { name: 'Transparent', value: 'transparent', iconcolor: 'transparent' },
        { name: 'White', value: 'white', iconcolor: 'bg-white' },
        { name: 'Background', value: 'bg', iconcolor: 'bg-body-1' },
        { name: 'Theme', value: 'theme', iconcolor: 'bg-theme-1' },
        { name: 'Accent', value: 'accent', iconcolor: 'bg-theme-accent-1' },
    ];



    return (
        <>
            <div className="offcanvas-header">
                <h5 className="offcanvas-title" id="theminglabel">Theming</h5>
                <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
            </div>
            <div className="offcanvas-body">


                {/* Color Theme Selection */}
                <div className="mb-4">
                    <h6 className="mb-0">Color Theme</h6>
                    <p className="text-secondary small mb-3">Choose the best suited color theme</p>
                    <div className="row g-2">
                        {colorThemes.map((theme) => (
                            <div key={theme.value} className="col-auto text-center">
                                <div
                                    className={`gradient-box ${colorTheme === theme.value ? 'active' : ''}`}
                                    onClick={() => setColorTheme(theme.value)}
                                >
                                    <div className={`avatar avatar-30 rounded bg-theme-1 theme-${theme.value}`}></div>
                                    {/* <p className="fs-12 text-center mt-1 mb-0">{theme.name}</p> */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Header Layout */}
                <div className="mb-3">
                    <h6 className="mb-0">Header Layout</h6>
                    <p className="text-secondary small mb-3">Change Header layout and colors</p>
                    <div className="row gx-2 gy-2">
                        {headerLayouts.map((layout) => (
                            <div key={layout.value} className="col-auto text-center">
                                <div
                                    className={`select-box ${headerLayout === layout.value ? 'active' : ''}`}
                                    onClick={() => setHeaderLayout(layout.value)}
                                    data-bs-toggle="tooltip" title={layout.name}
                                >
                                    <div className="avatar avatar-30 rounded text-theme-1">
                                        <i className={layout.icon}></i>
                                    </div>
                                    {/* <p className="fs-12 text-center mt-1 mb-0">{layout.name}</p> */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Header Style */}
                <div className="mb-4">
                    <div className="row gx-2 gy-2">
                        {headerStyles.map((style) => (
                            <div key={style.value} className="col-auto">
                                <div
                                    className={`select-box ${headerStyle === style.value ? 'active' : ''}`}
                                    onClick={() => setHeaderStyle(style.value)}
                                    data-bs-toggle="tooltip" title={style.name}
                                >
                                    <div className={`avatar avatar-30 rounded text-theme-1 ${style.iconcolor}`}>

                                    </div>
                                    {/* <p className="small text-center mt-1 mb-0">{style.name}</p> */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sidebar Layout */}
                <div className="mb-3">
                    <h6 className="mb-0">Sidebar Layout</h6>
                    <p className="text-secondary small mb-3">Change sidebar layout and colors</p>
                    <div className="row gx-2 gy-2">
                        {sidebarLayouts.map((layout) => (
                            <div key={layout.value} className="col-auto">
                                <div
                                    className={`select-box ${sidebarLayout === layout.value ? 'active' : ''}`}
                                    onClick={() => setSidebarLayout(layout.value)}
                                >
                                    <div className="avatar avatar-30 rounded text-theme-1">
                                        <i className={layout.icon}></i>
                                    </div>
                                    {/* <p className="small text-center mt-1 mb-0">{layout.name}</p> */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sidebar Style */}
                <div className="mb-4">
                    <div className="row gx-2 gy-2">
                        {sidebarStyles.map((style) => (
                            <div key={style.value} className="col-auto">
                                <div
                                    className={`select-box ${sidebarStyle === style.value ? 'active' : ''}`}
                                    onClick={() => setSidebarStyle(style.value)}
                                >
                                    <div className={`avatar avatar-30 rounded text-theme-accent-1 ${style.iconcolor}`}>

                                    </div>
                                    {/* <p className="small text-center mt-1 mb-0">{style.name}</p> */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Direction Toggle */}
                <div className="mb-4">
                    <h6 className="mb-0">Direction</h6>
                    <p className="text-secondary small mb-3">Step towards globalization</p>
                    <div className="row gx-2">
                        <div className="col-6">
                            <button
                                className={`btn btn-link w-100 ${direction === 'ltr' ? 'active' : ''}`}
                                onClick={() => setDirection('ltr')}
                            >
                                <i className={direction === 'ltr' ? 'bi bi-text-left me-2' : 'bi bi-text-left me-2'}></i>LTR
                            </button>
                        </div>
                        <div className="col-6">
                            <button
                                className={`btn btn-link w-100 ${direction === 'rtl' ? 'active' : ''}`}
                                onClick={() => setDirection('rtl')}
                            >
                                <i className={direction === 'rtl' ? 'bi bi-text-right me-2' : 'bi bi-text-right me-2'}></i>RTL
                            </button>
                        </div>
                    </div>
                </div>
                <br />
                <div className="text-center mb-4">
                    <Link to="/app/personalization" className="btn btn-sm btn-outline-theme">More options <i className="bi bi-arrow-right-short"></i></Link>
                </div>
            </div>
        </>
    );
};

export default Theming;

